import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formDataCheck } from '../../../shared-methods/monitoring-toolkit-methods';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import { DatePicker } from '@components-clean/core/date-picker';
import EditCard from '@components-clean/core/edit-card';
import Loading from '@components-clean/core/loading/loading';
import { TextArea } from '@components-clean/core/text-area';
import { TextBox } from '@components-clean/core/text-box';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { assumeUtc } from '@utils-clean/date-time-helpers';
import { javascriptDateWithoutTimeFromUTC } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import SiteDatasetReviewsApi from '@apis/bff-api/central-monitoring/site-dataset-reviews';

interface IProps {
    studyRef: string;
    canEdit: boolean;
    siteId?: number;
    dataset?: CM2Dataset;
}

export const COMPONENT_NAME = 'site-current-card';

const CurrentCard = ({ studyRef, canEdit, siteId, dataset }: IProps) => {
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.siteDatasetReview.reviewCard',
    });

    const { t: tCommon } = useTranslation('common');

    interface IFormData {
        isReviewed: boolean;
        startDate?: Date | null;
        endDate?: Date | null;
        reviewerName?: string;
        summary?: string;
    }

    interface IFormValidationConfig {
        reviewerName: {
            maxLength: number;
        };
        summary: {
            maxLength: number;
        };
    }

    const formValidationConfig: IFormValidationConfig = {
        reviewerName: {
            maxLength: 100,
        },
        summary: {
            maxLength: 3000,
        },
    };

    const EMPTY_FORM: IFormData = {
        isReviewed: false,
        reviewerName: '',
        summary: '',
        startDate: null,
        endDate: null,
    };

    const { data: currentReview, isLoading: isFetching } = SiteDatasetReviewsApi.useGet(studyRef, siteId, dataset?.id);

    const { trigger: saveReview, isMutating: isSaving } = SiteDatasetReviewsApi.useSave(
        studyRef,
        siteId!,
        dataset?.id ?? 0,
    );

    const isLoading = isFetching || isSaving;

    const [defaultForm, setDefaultForm] = useState<IFormData>(EMPTY_FORM);
    const [formData, setFormData] = useState<IFormData>(EMPTY_FORM);
    const [isEditing, setIsEditing] = useState(false);

    const resetForm = () => {
        if (!currentReview) {
            setFormData(EMPTY_FORM);
            setDefaultForm(EMPTY_FORM);
            return;
        }
        const startDate = currentReview.startDate ? javascriptDateWithoutTimeFromUTC(currentReview.startDate) : null;
        const completedDate = currentReview.completedDate
            ? javascriptDateWithoutTimeFromUTC(currentReview.completedDate)
            : null;

        const unsetForm: IFormData = {
            isReviewed: currentReview.reviewed ?? false,
            startDate,
            endDate: completedDate,
            reviewerName: currentReview.reviewer ?? undefined,
            summary: currentReview.summary ?? undefined,
        };
        setFormData(unsetForm);
        setDefaultForm(unsetForm);
    };

    // Reset the form when the current review changes
    useEffect(() => {
        resetForm();
        setIsEditing(false);
    }, [currentReview, dataset]);

    // Reset the form when the form is no longer being edited
    useEffect(() => {
        resetForm();
    }, [isEditing]);

    const dataUnchanged = useMemo(() => {
        return formDataCheck<IFormData>(formData, defaultForm);
    }, [formData, defaultForm]);

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: !dataUnchanged,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const startDateEndDateValid = () => {
        if (!formData.startDate || !formData.endDate) return true;

        return formData.startDate <= formData.endDate;
    };

    const isValid = (form: IFormData): boolean => {
        if ((form.reviewerName?.length ?? 0) > formValidationConfig.reviewerName.maxLength) {
            return false;
        }
        if ((form.summary?.length ?? 0) > formValidationConfig.summary.maxLength) {
            return false;
        }

        return startDateEndDateValid();
    };

    const onSaveHandler = async () => {
        try {
            if (!isValid(formData)) {
                return;
            }

            await saveReview({
                reviewed: formData.isReviewed,
                startDate: formData.startDate ? assumeUtc(formData.startDate) : undefined,
                completedDate: formData.endDate ? assumeUtc(formData.endDate) : undefined,
                reviewer: formData.reviewerName,
                summary: formData.summary,
            });
            ShowSuccess(tRoot('actionsSuccessfulToast'));
            await SiteDatasetReviewsApi.invalidateGetAll(studyRef, siteId!);
            setIsEditing(false);
        } catch (e) {
            ShowError('An error occurred while trying to save this review. Please try again later.');
            console.error('Failed to save dataset review.', e); // eslint-disable-line
        }
    };

    const onCancelHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        setIsEditing(false);
    };

    return (
        <EditCard
            className="card review-card"
            data-testid="review-card"
            title={`${t('title')}${!!dataset ? `: ${dataset.dataCutDate}` : ''}`}
            onEdit={() => setIsEditing(true)}
            onSave={onSaveHandler}
            onCancel={onCancelHandler}
            isEditMode={isEditing}
            disabled={!dataset || !siteId || !canEdit || isLoading}
            saveDisabled={dataUnchanged || !isValid(formData)}
        >
            <div className="top">
                <div className="checkbox-area">
                    <CoreCheckbox
                        data-testid="is-reviewed"
                        label={t('labels.reviewed')}
                        checked={formData.isReviewed}
                        disabled={!isEditing}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                isReviewed: e.value,
                            })
                        }
                    />
                </div>
                <div className="date">
                    <DatePicker
                        label={t('labels.reviewStartDate')}
                        data-testid="review-start-date"
                        fillMode="solid"
                        readonly={!isEditing}
                        value={formData.startDate}
                        valid={startDateEndDateValid()}
                        validationMessage={t('validation.reviewStartDateBeforeEndDate')}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                startDate: e.value,
                            })
                        }
                    />
                </div>
                <div className="date">
                    <DatePicker
                        label={t('labels.reviewEndDate')}
                        data-testid="review-end-date"
                        fillMode="solid"
                        readonly={!isEditing}
                        value={formData.endDate}
                        valid={startDateEndDateValid()}
                        validationMessage={t('validation.reviewEndDateAfterBeforeDate')}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                endDate: e.value,
                            })
                        }
                    />
                </div>
            </div>
            <div>
                <TextBox
                    label={t('labels.reviewer')}
                    data-testid="reviewer"
                    readOnly={!isEditing}
                    value={formData.reviewerName}
                    valid={(formData.reviewerName?.length ?? 0) <= formValidationConfig.reviewerName.maxLength}
                    validationMessage={tCommon('limitExceeded')}
                    maxLength={formValidationConfig.reviewerName.maxLength}
                    onChange={(e) =>
                        setFormData({
                            ...formData,
                            reviewerName: e.value as string,
                        })
                    }
                />
            </div>
            <div className="bottom">
                <TextArea
                    label={t('labels.reviewSummary')}
                    data-testid="review-summary"
                    readOnly={!isEditing}
                    value={formData.summary}
                    maxLength={formValidationConfig.summary.maxLength}
                    valid={(formData.summary?.length ?? 0) <= formValidationConfig.summary.maxLength}
                    validationMessage={tCommon('limitExceeded')}
                    onChange={(e) =>
                        setFormData({
                            ...formData,
                            summary: e.value,
                        })
                    }
                />
            </div>
            <div className={`loading-overlay ${!isLoading ? 'fade-out' : ''}`}>
                <Loading />
            </div>
        </EditCard>
    );
};

export default CurrentCard;
